<template>
  <div class="result-view">
    <section class="section section--booking-engine">
      <booking-engine size="sm" @search="onSearch" />
    </section>

    <b-container>
      <section v-if="loading" class="section section--loader">
        <loader size="large" :show_timing_slot="true" />
      </section>

      <section v-else class="section section--filters">
        <b-row>
          <b-col
            cols="3"
            class="filters__sticky-wrapper"
            :class="{ visible: show_filters }"
          >
            <div class="result-view--sticky">
              <filters
                :filtered_all_carriers="filtered_flights.filtered_all_carriers"
                :flights_count="
                  ((filtered_flights || []).filtered || []).length
                "
                :is-open="show_filters"
                @toggle-filters="toggle_filters"
              />
            </div>
          </b-col>

          <b-col xs="12">
            <div
              class="flightoffer"
              v-if="Object.keys(this.$store.state.liveflight.results).length"
            >
              <div class="flightoffer--wrap" v-if="filtered_flights.filtered">
                <div v-if="filtered_flights.filtered.length === 0">
                  <no-results />
                  <no-results-suggestions />
                </div>
                <div v-else>
                  <div class="filters__btn-container">
                    <vb-button
                      class="filters__btn-toggle variant-white"
                      :vb_options="{
                        size: 'sm',
                        icon: 'filters',
                        text: 'Filtri',
                      }"
                      @click="toggle_filters"
                    >
                    </vb-button>
                    <vb-dropdown
                      class="filters__btn-order variant-white"
                      :vb_options="{
                        text: sorting.find((s) => s.by === current_sort)
                          .description,
                        size: 'sm',
                        right: true,
                      }"
                    >
                      <b-dropdown-item
                        v-for="(s, index) in sorting"
                        :key="_uid + 'sort' + index"
                        :active="current_sort === s.by"
                        @click="
                          () => {
                            set_sorting(s.by);
                            scroll_top();
                          }
                        "
                      >
                        Ordina per {{ s.title.toLowerCase() }}
                      </b-dropdown-item>
                    </vb-dropdown>
                  </div>
                  <dynamic-scroller
                    page-mode
                    ref="resultsListScroller"
                    :items="items"
                    :item-size="null"
                    :min-item-size="100"
                    key-field="id"
                    class="scroller"
                  >
                    <template v-slot="{ item, index, active }">
                      <dynamic-scroller-item
                        :item="item"
                        class="pb-4"
                        :data-index="index"
                        :class="'flight--pad-' + index"
                        :active="active"
                      >
                        <component
                          v-if="item.component"
                          :is="item.component"
                          v-bind="item.props"
                        />
                        <flight-pad
                          v-else
                          :flight_offer="item"
                          :flight_offer_index="index"
                          :is_best="
                            Object.keys(best_picks).reduce(
                              (list, pick) =>
                                best_picks[pick].flight_offer_id === item.id
                                  ? [...list, pick]
                                  : list,
                              [],
                            )
                          "
                          selectable
                        />
                      </dynamic-scroller-item>
                    </template>
                  </dynamic-scroller>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </section>
    </b-container>
  </div>
</template>

<script>
import BookingEngine from "@liveflight/components/blocks/booking-engine/booking-engine";
import FlightPad from "@liveflight/components/blocks/flight-pad/flight-pad";
import Filters from "@liveflight/components/blocks/filters/filters";
import NoResults from "@liveflight/components/blocks/no-results";
import NoResultsSuggestions from "@liveflight/components/blocks/no-results-suggestions";
import { filter_flight_offers } from "@liveflight/utilities/filters";
import {
  parse_flight_offer_for_ecommerce_item,
  parse_flight_offers,
} from "@liveflight/utilities/mixins";
import { custom_event } from "@/utilities/commons";
import { service_handler } from "@/services";
import {
  sort_flight_offers,
  set_sorting,
  get_sorting,
} from "@liveflight/utilities/sorting";
import { add_extra_components_to_results } from "@/utilities";
import MarketingPad from "@/components/blocks/marketing-pad.vue";
import { sorting_options } from "@liveflight/utilities/sorting";

export default {
  name: "liveflight-search",
  components: {
    "flight-pad": FlightPad,
    "booking-engine": BookingEngine,
    filters: Filters,
    "no-results": NoResults,
    "no-results-suggestions": NoResultsSuggestions,
    "marketing-pad": MarketingPad,
  },
  props: {},
  data() {
    return {
      show_filters: false,
    };
  },
  watch: {},
  computed: {
    filter_round_trip_departure: {
      get() {
        return this.$store.state.liveflight.filter_round_trip_departure;
      },
      set() {
        //
      },
    },
    sorting() {
      return Object.entries(sorting_options).map(([key, value]) => ({
        by: key,
        ...value,
      }));
    },
    filter_round_trip_arrival: {
      get() {
        return this.$store.state.liveflight.filter_round_trip_arrival;
      },
      set() {
        //
      },
    },
    filter_carriers: {
      get() {
        return this.$store.state.liveflight.filter_carriers;
      },
      set() {
        //
      },
    },
    offers: {
      get() {
        return this.$store.state.liveflight.results.offers;
      },
      set() {},
    },
    best_picks: {
      get() {
        return this.$store.state.liveflight.results.best_picks || {};
      },
      set() {},
    },
    loading: {
      get() {
        return this.$store.state.liveflight.loading;
      },
      set() {},
    },
    current_sort() {
      return get_sorting();
    },
    items() {
      return this.add_extra_components_to_results(
        this.filtered_flights.filtered,
        "id",
      );
    },
    filtered_flights: {
      get() {
        let filtered_flights = filter_flight_offers(),
          filtered = sort_flight_offers(
            filtered_flights.filtered,
            (this.best_picks["best"] || {}).rankings || {},
          );

        custom_event({
          event: "view_item_list",
          ecommerce: {
            currency: "EUR",
            items: this.parseGa4EcommerceItems(filtered),
            transaction_id: "",
            affiliation: "",
            value: 0, // Sum of (price * quantity) for all items.
            tax: 0,
            shipping: 0,
            coupon: "",
          },
        });

        return {
          filtered: filtered,
          filtered_all_carriers: filtered_flights.filtered_all_carriers,
        };
      },
      set() {},
    },
  },
  mounted() {
    // Check if this is a new search from booking engine (redirect-then-search pattern)
    if (this.$store.state.liveflight.trigger_search) {
      this.executeSearch();
    }
    // Handle page refresh - if we have a search_request stored, re-execute the search
    else if (Object.keys(this.$store.state.liveflight.results).length === 0) {
      const searchRequest = this.$store.state.liveflight.search_request;
      if (searchRequest && Object.keys(searchRequest).length > 0) {
        // Re-trigger search on page refresh
        this.$store.commit("liveflight/set_loading", true);
        this.executeSearch();
      }
      // If no search_request, user stays on page and sees empty state / no results message
    }
  },
  methods: {
    set_sorting,
    add_extra_components_to_results,
    parseGa4EcommerceItems(results) {
      // results = filtered
      const items = [];
      results.forEach((flightOffer) => {
        items.push(parse_flight_offer_for_ecommerce_item(flightOffer));
      });
      return items;
    },
    executeSearch() {
      const request = this.$store.state.liveflight.search_request;
      if (!request || Object.keys(request).length === 0) {
        this.$store.commit("liveflight/end_search");
        return;
      }

      const params = {
        getFlightOffersBody: { ...request },
      };

      service_handler("flightOffers", params)
        .then((response) => {
          let parsed_flight_offers = parse_flight_offers(response.data);
          this.$store.commit("liveflight/results", parsed_flight_offers);

          // Handle suggestions if no results
          if (
            this.$config.guiSettings.enableNoResultsSearchSuggestions !==
              false &&
            this.$store.state.liveflight.can_search_suggestions &&
            parsed_flight_offers.flights.length === 0
          ) {
            this.handleSearchSuggestions();
          }
        })
        .finally(() => {
          this.$store.commit("liveflight/end_search");
        });
    },
    onSearch() {
      // When searching from results page, just execute the search (already on results page)
      this.executeSearch();
    },
    toggle_filters() {
      this.show_filters = !this.show_filters;
      if (this.show_filters) {
        document.body.classList.add("overflow-hidden");
      } else {
        document.body.classList.remove("overflow-hidden");
      }
    },
    handleSearchSuggestions() {
      this.$store.commit("liveflight/can_search_suggestions", false);
      this.$store.commit("liveflight/suggestions", {});

      const { search_request: request = {} } =
        this.$store.state.liveflight;
      if (Object.keys(request).length === 0) {
        return;
      }

      // TODO: check documentation on how to do suggestions for multi-itinerary
      const { originDestinations = [] } = request;
      if (originDestinations.length === 0 || originDestinations.length > 2) {
        return;
      }

      this.$store.commit("liveflight/set_loading", true);

      let request_by_radius = JSON.parse(JSON.stringify(request)),
        request_by_time_window = JSON.parse(JSON.stringify(request));

      request_by_radius.originDestinations.forEach((o) => {
        o.originRadius = 100;
        o.destinationRadius = 100;
      });

      if (
        request_by_radius.searchCriteria &&
        request_by_radius.searchCriteria.flightFilters
      ) {
        request_by_radius.searchCriteria.flightFilters.returnToDepartureAirport = false;
      }

      request_by_time_window.originDestinations.forEach((o) => {
        o.departureDateTimeRange.dateWindow = "I3D";
      });

      const search_suggestion = (getFlightOffersBody) =>
        service_handler(
          "flightOffers",
          {
            getFlightOffersBody,
          },
          {
            modal: false,
          },
        );

      Promise.all([
        search_suggestion(request_by_radius),
        search_suggestion(request_by_time_window),
      ])
        .then(
          (response) => {
            this.$store.commit("liveflight/suggestions", {
              by_radius: parse_flight_offers(response[0].data),
              by_time_window: parse_flight_offers(response[1].data),
            });
          },
          () => {
            this.$store.commit("liveflight/suggestions", {});
          },
        )
        .finally(() => {
          this.$store.commit("liveflight/set_loading", false);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.result-view--initial-loading {
  padding: 3rem 0 4rem 0;
  @include media-breakpoint-up(lg) {
    padding: 6rem 0 8rem 0;
  }
}

.result-view--sticky {
  position: sticky;
  top: 1rem;
}

.filters {
  &__sticky-wrapper {
    align-self: flex-start;
    top: 0;
    min-height: 100vh;
    overflow-y: scroll;
    box-shadow: 10px 0 20px 0 rgba(0, 0, 0, 0.3);
    width: 100%;
    z-index: 999;

    @include hide-scrollbar;

    @include media-breakpoint-up(xl) {
      box-shadow: none;
      z-index: 0;
    }

    @include media-breakpoint-down(xl) {
      max-width: none;
      min-height: auto;
      align-self: auto;
      transform: translateX(100%);
      transition: $transition-base;
      display: flex;
      flex-direction: column;
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      overflow: hidden;
      background-color: $white;

      &.visible {
        transform: translateX(0);
      }
    }
  }

  &__head {
    margin-bottom: $grid-gutter-width/2;

    @include media-breakpoint-down(lg) {
      display: flex;
      justify-content: space-between;
      background: $white;
      padding: $grid-gutter-width/2 0;
      z-index: 2;
      border-bottom: 1px solid $gray-200;
      min-height: 55px;
      margin-bottom: 0;
    }

    &-element {
      margin-bottom: 0;
      line-height: normal;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    &-title {
      font-size: $font-size-lg;
      color: var(--primary);
      font-weight: $vb-font-weight-semibold;
      display: inline-block;
    }
  }

  &__btn {
    &-toggle {
      @include media-breakpoint-up(xl) {
        display: none;
      }
    }

    &-container {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: $grid-gutter-width / 2;
      margin-bottom: $grid-gutter-width / 2;

      @include media-breakpoint-up(md) {
        justify-content: flex-end;
      }
    }
  }
}

.section {
  &--booking-engine {
    margin: 0;
    position: relative;

    &:after {
      content: "";
      position: absolute;
      top: 0;
      bottom: 50%;
      left: 0;
      right: 0;
      background-color: var(--extra1); // same as header
    }
  }
  &--filters {
    position: relative;

    @at-root .overflow-hidden & {
      z-index: 999;
    }
  }
}

.flightoffer--itineraries {
  height: inherit;
}
</style>
